/**
 * Backend messages, in the language the branch works in.
 *
 * The API answers in English on purpose — it is a contract other clients and its own
 * tests read — and this web client is the only Thai-facing consumer of it, so the
 * translation belongs here rather than in the response enum.
 *
 * Keyed on the EXACT message the API sends. That is deliberate over a pattern match: a
 * reworded backend message simply falls through and shows the English original, which is
 * ugly but honest, where a loose match would quietly attach the wrong Thai sentence to a
 * rule that had changed underneath it. If a message stops translating, the backend text
 * moved — update the key, do not relax the match.
 *
 * Only messages a user actually hits belong here. Diagnostics they can do nothing about
 * ("Failed to retrieve list of…") stay English so a support screenshot still names the
 * failing call.
 */
const THAI_MESSAGES: Record<string, string> = {
  "Sales report not found for this bill":
    "Bill ใบนี้ยังไม่ถูกสร้างเป็น Sale Report",

  // ปฏิเสธคำขอยกเลิก — the bill was superseded and its replacement is still live (R9).
  "Cannot reject: replacement transaction is still active; cancel it first":
    "ไม่สามารถปฏิเสธได้ เนื่องจากใบออกแทนของบิลนี้ยังใช้งานอยู่ กรุณายกเลิกใบออกแทนก่อน",

  // แก้ไขการชำระเงิน — the whole family, because a user meets them in one dialog and a
  // half-translated dialog reads worse than an untranslated one.
  "Failed to update the payment channel or payment status.":
    "แก้ไขช่องทางหรือสถานะการชำระเงินไม่สำเร็จ",
  "Provide a payment channel, a payment status, or the amount received to update.":
    "กรุณาระบุช่องทางการชำระเงิน สถานะการชำระเงิน หรือยอดรับจริงที่ต้องการแก้ไข",
  "The amount actually received can only be recorded on a cash or bank-transfer bill.":
    "บันทึกยอดรับจริงได้เฉพาะบิลที่ชำระด้วยเงินสดหรือเงินโอนเท่านั้น",
  "The bank has confirmed a QR payment for this bill, so the amount received is fixed by the bank.":
    "ธนาคารยืนยันการชำระด้วย QR ของบิลนี้แล้ว ยอดรับจริงจึงเป็นไปตามยอดที่ธนาคารยืนยัน",
  "The payment channel and status can only be changed on the bill's own business day.":
    "แก้ไขช่องทางและสถานะการชำระเงินได้เฉพาะภายในวันที่ของบิลเท่านั้น",
  "The back office has already reviewed this day's deposit; the payment channel and status are locked.":
    "รายงานการโอนของวันนี้ถูกตรวจแล้ว ไม่สามารถแก้ไขช่องทางและสถานะการชำระเงินได้",
  "The payment channel and status cannot be changed on a cancelled bill.":
    "บิลนี้ถูกยกเลิกแล้ว ไม่สามารถแก้ไขช่องทางและสถานะการชำระเงินได้",

  // สาขา — creating/editing a branch and opening its routes (เปิดใช้).
  "Branch number, prefix or code is already used.":
    "เลขสาขา ตัวย่อ หรือรหัสสาขานี้ถูกใช้โดยสาขาอื่นแล้ว",
  "Express document code is already used by another branch.":
    "รหัสเอกสาร Express นี้ถูกใช้โดยสาขาอื่นแล้ว",
  "Special route code is already used.":
    "รหัสสายพิเศษ (ตัวย่อ + 99) ของตัวย่อนี้ถูกใช้แล้ว",
  "Route is not ready to open.": "สายนี้ยังไม่พร้อมเปิดใช้ ยังขาดรายการบังคับ",

  // ตั้งค่าผู้ใช้ / รถ
  "A user with this username or email already exists.":
    "ชื่อผู้ใช้หรืออีเมล์นี้มีอยู่ในระบบแล้ว",
  "The route does not belong to the selected branch.":
    "สายที่เลือกไม่ได้อยู่ในสาขานี้",
  "A sales person must be assigned a route.": "กรุณาเลือกสายของพนักงานขาย",
  "This ID number is already registered to another staff member.":
    "เลขบัตรประชาชนนี้ถูกใช้กับพนักงานคนอื่นแล้ว",
  "The user could not be fully saved and the change could not be rolled back. Please fix the user manually.":
    "บันทึกผู้ใช้ไม่สำเร็จ และระบบย้อนการเปลี่ยนแปลงบัญชีผู้ใช้ไม่ได้ กรุณาแจ้งผู้ดูแลระบบให้ตรวจสอบบัญชีนี้",
  "The resource already exists.": "ข้อมูลนี้มีอยู่ในระบบแล้ว",
};

/** The Thai wording for a backend message, or the message itself when none is written. */
export const localizeApiMessage = (message: string): string =>
  THAI_MESSAGES[message] ?? message;
